import { applyFluidBoardRect, resetBoardScrollMargin } from "@/components/utils/boardRect";
import { calcBoardRect } from "@/math/coordinate-utils";
import { useBoardStore } from "@/store/board";
import { useZoomStore } from "@/store/zoom";
import type { ElementState } from "@/utils/dom/dom";
import { hideElements, showElements } from "@/utils/dom/dom";
import { disconnectObservers, reconnectObservers } from "@/utils/dom/mutationObserver";
import { nextTicks } from "@/utils/vue";

import type { ZoomLayer } from "./ZoomLayer";

// for a small number of stickies, it's faster to NOT use the zoom layer
const useZoomLayerStickyNumber = 100;

export function zoomHandler(zoomLayer: ZoomLayer) {
  let zoomFactor = 0;
  let cardNodes: ElementState | null = null;
  let checkZoomTimeout = 0;
  const store = useZoomStore();

  return { startZoom, endZoom };

  function startZoom() {
    if (useBoardStore().isCurrentBoardFluid) {
      disconnectObservers();
      zoomFactor = store.factor;
      const useLayer =
        Object.keys(useBoardStore().currentBoard().cards).length > useZoomLayerStickyNumber;
      if ((store.useZoomLayer = useLayer)) {
        initZoomLayer();
      }
      checkZoom();
    }
  }

  function endZoom() {
    if (useBoardStore().isCurrentBoardFluid) {
      // Make sure the latest zoom was registered, but do not trigger recursive checks
      checkZoom(true);
      store.updateZoomFactor();
      resetBoardScrollMargin();
      if (store.useZoomLayer) {
        hideZoomLayer();
      }
      window.cancelAnimationFrame(checkZoomTimeout);
      // 3 is found by trial: check that the last tick takes (nearly) no time
      nextTicks(3, reconnectObservers);
    }
  }

  /**
   * Checks if the zoom factor has changed and updates the board size accordingly
   * By default, called recursively with requestAnimationFrame
   *
   * @param stop if true, stops the recursive call (stop is a number when triggered through requestAnimationFrame)
   */
  function checkZoom(stop?: boolean | number) {
    if (zoomFactor !== store.dynamicFactor) {
      zoomFactor = store.dynamicFactor;
      const rect = calcBoardRect(zoomFactor);
      applyFluidBoardRect(rect);
      store.scroller.scroll(rect);
    }

    if (stop !== true) {
      checkZoomTimeout = window.requestAnimationFrame(checkZoom);
    }
  }

  function initZoomLayer() {
    zoomLayer.paintCards(useBoardStore().currentBoard());
    // removing and adding cards directly using DOM (.1ms, .75ms) is around 2x faster that using vue.js (.2ms, 1.5ms)
    cardNodes = hideElements(document.querySelectorAll(".card,.sticky-note"));
  }

  function hideZoomLayer() {
    if (cardNodes) {
      showElements(cardNodes);
      cardNodes = null;
    }
    zoomLayer.hide();
  }
}
